import { QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';

import { Button } from '@/componentes/ui/button';
import { Toaster } from '@/componentes/ui/sonner';
import { TooltipProvider } from '@/componentes/ui/tooltip';
import { criarQueryClient, mensagemDoErro, useGarantirSessao } from '@/dados';
import { BarreiraDeErro } from '@/layout/barreira-de-erro';

import { Rotas } from './rotas';

/* App — providers, guarda de sessão e rotas. Nada mais.
 *
 * Ordem importa: o QueryClient tem de existir antes da guarda de sessão, que
 * é quem faz a primeira chamada da aplicação. */

export function App() {
  // useState com função: um QueryClient por montagem, nunca um por render.
  const [clienteQuery] = React.useState(criarQueryClient);

  return (
    <QueryClientProvider client={clienteQuery}>
      <BrowserRouter>
        <TooltipProvider delayDuration={250}>
          <GuardaSessao>
            {/* Dentro do router: a barreira precisa da rota para se recompor
                sozinha quando o usuário navega para longe da tela quebrada. */}
            <BarreiraComRota />
          </GuardaSessao>
          {/* Fila de avisos. role=status vem do sonner. */}
          <Toaster position="bottom-right" closeButton richColors={false} />
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/** A rota vira a chave da barreira: navegar para outra tela limpa o erro. */
function BarreiraComRota() {
  const { pathname } = useLocation();
  return (
    <BarreiraDeErro chave={pathname}>
      <Rotas />
    </BarreiraDeErro>
  );
}

/* Guarda de sessão — NÃO é tela de login.
 *
 * Quem abre a ferramenta já pode usar: se não há cookie de sessão, cria-se uma
 * sessão anônima e segue. Login existe para quem quer acessar de outro
 * aparelho, e é upgrade do mesmo usuário. */
function GuardaSessao({ children }: { children: React.ReactNode }) {
  const { pronto, carregando, erro } = useGarantirSessao();

  if (pronto) return <>{children}</>;

  if (erro && !carregando) {
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="bg-card border-border grid max-w-md gap-3 rounded-xl border p-6 text-center">
          <h1 className="text-secao">Não foi possível iniciar</h1>
          <p className="text-muted-foreground text-apoio">{mensagemDoErro(erro)}</p>
          <div>
            <Button onClick={() => window.location.reload()}>Tentar de novo</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen place-items-center">
      <p className="text-muted-foreground text-apoio" role="status">
        Carregando…
      </p>
    </div>
  );
}
